import { type Component, truncateToWidth, visibleWidth } from "@kolisachint/hoocode-tui";
import type { AgentSession } from "../../../core/agent-session.js";
import { sumAssistantUsage } from "../../../core/agent-session-stats.js";
import { BRAND_MARK, GIT_BRANCH_GLYPH } from "../../../core/brand.js";
import type { ReadonlyFooterDataProvider } from "../../../core/footer-data-provider.js";
import { formatTokens } from "../../../core/format-tokens.js";
import { type StartupProgress, startupProgress } from "../../../core/startup-progress.js";
import { taskStore } from "../../../core/task-store.js";
import { DEFAULT_TOOL_OUTPUT_VIEW, type ToolOutputView } from "../../../core/tool-output-view.js";
import { theme } from "../theme/theme.js";
import { renderDownloadProgress, renderProgressBar } from "./progress-bar.js";
import { sessionChipFits } from "./session-chip.js";

/**
 * Assemble one footer line: `left` flush left, `right` flush right when it fits
 * (≥2 cols between), padded to the full width. When it doesn't fit, drop `right`
 * and pad; when even `left` overflows, truncate it. Width math runs on the plain
 * strings; the styled strings carry the colour. Every returned line is exactly
 * `width` cells or fewer — the invariant the footer-width tests hold us to.
 */
function assembleLine(width: number, leftPlain: string, leftStyled: string, rightPlain = "", rightStyled = ""): string {
	const lw = visibleWidth(leftPlain);
	if (rightPlain && lw + 2 + visibleWidth(rightPlain) <= width) {
		return leftStyled + " ".repeat(width - lw - visibleWidth(rightPlain)) + rightStyled;
	}
	if (lw <= width) return leftStyled + " ".repeat(width - lw);
	return truncateToWidth(leftStyled, width, theme.fg("dim", "…"));
}

/** A compact context-fill gauge, coloured by proximity to the auto-compact trip point. */
function contextGauge(percent: number, errorLevel: number, warnLevel: number): { plain: string; styled: string } {
	const CELLS = 8;
	const filled = Math.max(0, Math.min(CELLS, Math.round((percent / 100) * CELLS)));
	const fill = "▰".repeat(filled);
	const track = "▱".repeat(CELLS - filled);
	const color = percent >= errorLevel ? "error" : percent >= warnLevel ? "warning" : "accent";
	// The unfilled track takes `halftone`, which falls back to `dim` — the tone it
	// used before the token existed. The point of separating them is that `dim` is
	// body-weight text: a track drawn in it reads as writing rather than as the
	// space the fill has yet to reach.
	return { plain: fill + track, styled: theme.fg(color, fill) + theme.fg("halftone", track) };
}

/**
 * The view dial's glyph, filling up as the view widens — so the footer shows
 * where the dial sits on its three-stop scale, not just its name.
 */
const TOOL_OUTPUT_VIEW_GLYPHS: Record<ToolOutputView, string> = {
	radar: "◌",
	peek: "◍",
	full: "◉",
};

/** Count subagent runs currently in flight, for the footer's live delegation cue. */
function activeSubagentCount(): number {
	return taskStore.list().filter((t) => t.source === "subagent" && t.status === "in_progress").length;
}

/**
 * Sanitize text for display in a single-line status.
 * Removes newlines, tabs, carriage returns, and other control characters.
 */
function sanitizeStatusText(text: string): string {
	// Replace newlines, tabs, carriage returns with space, then collapse multiple spaces
	return text
		.replace(/[\r\n\t]/g, " ")
		.replace(/ +/g, " ")
		.trim();
}

/**
 * One footer line for a transient progress entry (tool download, index build,
 * `/learn` reading transcripts). The bar comes from the shared progress-bar
 * component, so this line and the voice panel's cannot drift apart again. An
 * error entry renders as a dim message instead. Returns a styled string; the
 * caller width-clamps it.
 */
function renderStartupLine(entry: StartupProgress): string {
	if (entry.kind === "error") {
		return theme.fg("dim", `${entry.label}: ${entry.message}`);
	}
	const label = theme.fg("text", entry.label);
	if (entry.kind === "download") {
		return `${label} ${renderDownloadProgress(entry.receivedBytes, entry.totalBytes)}`;
	}
	const ratio = entry.total > 0 ? entry.done / entry.total : 0;
	return `${label} ${renderProgressBar(ratio, `${entry.done}/${entry.total} ${entry.unit}`)}`;
}

/**
 * Footer component that shows pwd, token stats, and context usage.
 * Computes token/context stats from session, gets git branch and extension statuses from provider.
 */
/** How many rows the footer takes: everything it has, or the one-row strip. */
export type FooterDensity = "full" | "line";

export class FooterComponent implements Component {
	private autoCompactEnabled = true;
	private toolOutputView: ToolOutputView = DEFAULT_TOOL_OUTPUT_VIEW;
	private sessionChipShown = false;

	constructor(
		private session: AgentSession,
		private footerData: ReadonlyFooterDataProvider,
	) {}

	setSession(session: AgentSession): void {
		this.session = session;
	}

	private density: FooterDensity = "full";

	setAutoCompactEnabled(enabled: boolean): void {
		this.autoCompactEnabled = enabled;
	}

	/** The view dial's current stop, shown so `alt+o` has somewhere to land. */
	setToolOutputView(view: ToolOutputView): void {
		this.toolOutputView = view;
	}

	/**
	 * How many rows the footer is allowed.
	 *
	 * `line` keeps the mark, the mode, the context gauge and the model — what
	 * you glance at — and gives up the path, the branch, the token arrows and
	 * the cost, which are what you look at deliberately and can get back with
	 * one press of the dial. It is a different line, not line 2 with line 1
	 * deleted: dropping a row would lose the mode, which is the single most
	 * consequential thing the footer says.
	 */
	setDensity(density: FooterDensity): void {
		this.density = density;
	}

	/**
	 * Whether the input box is carrying a session chip. When it is, line 1 drops
	 * its own copy of the name: saying it twice, six rows apart, teaches the eye
	 * to ignore both, and line 1 is the line that runs out of width first.
	 */
	setSessionChipShown(shown: boolean): void {
		this.sessionChipShown = shown;
	}

	/**
	 * No-op: git branch caching now handled by provider.
	 * Kept for compatibility with existing call sites in interactive-mode.
	 */
	invalidate(): void {
		// No-op: git branch is cached/invalidated by provider
	}

	/**
	 * Clean up resources.
	 * Git watcher cleanup now handled by provider.
	 */
	dispose(): void {
		// Git watcher cleanup handled by provider
	}

	render(width: number): string[] {
		const state = this.session.state;

		// Cumulative usage across ALL session entries (not just post-compaction
		// messages). Shares sumAssistantUsage with the per-request cost line the
		// transcript prints at agent_end, so the footer total and that line are always
		// derived from the same accounting.
		const {
			input: totalInput,
			output: totalOutput,
			cacheRead: totalCacheRead,
			cacheWrite: totalCacheWrite,
			cost: totalCost,
		} = sumAssistantUsage(this.session.sessionManager.getEntries());

		// Calculate context usage from session (handles compaction correctly).
		// After compaction, tokens are unknown until the next LLM response.
		const contextUsage = this.session.getContextUsage();
		const contextWindow = contextUsage?.contextWindow ?? state.model?.contextWindow ?? 0;
		const contextPercentValue = contextUsage?.percent ?? 0;
		const contextPercent = contextUsage?.percent !== null ? contextPercentValue.toFixed(1) : "?";

		// Replace home directory with ~
		let pwd = this.session.sessionManager.getCwd();
		const home = process.env.HOME || process.env.USERPROFILE;
		if (home && pwd.startsWith(home)) {
			pwd = `~${pwd.slice(home.length)}`;
		}
		const branch = this.footerData.getGitBranch();
		// The name is shown here only when the chip is not carrying it — either no
		// chip is set, or the terminal is too narrow for one, in which case the
		// footer is the only place left that says which session this is.
		const sessionName =
			this.sessionChipShown && sessionChipFits(width) ? undefined : this.session.sessionManager.getDisplayName();
		const modeLabel = this.footerData.getActiveMode();

		// ── Line 1 — identity & location ────────────────────────────────────────
		// Lead with the brand mark + MODE (the agent's guardrail: Ask/Plan/Build/
		// Debug) in bold accent so it is the first thing the eye lands on, then the
		// path, git branch, and session name in descending emphasis. The live
		// subagent count sits flush right — present only while work is delegated.
		const modeUp = modeLabel.toUpperCase();
		// Themes that define the brandBg/brandText pair paint the mark as a filled
		// chip; the padding lives in the plain string too so width math and
		// truncation stay honest. Themes without the pair keep accent-coloured text.
		const chip = theme.hasBg("brandBg") && theme.has("brandText");
		const brand = chip ? ` ${BRAND_MARK} ${modeUp} ` : `${BRAND_MARK} ${modeUp}`;
		let l1Plain = `${brand}  ${pwd}`;
		const brandStyled = chip
			? theme.bg("brandBg", theme.bold(theme.fg("brandText", brand)))
			: theme.bold(theme.fg("accent", brand));
		let l1Styled = `${brandStyled}  ${theme.fg("muted", pwd)}`;
		if (branch) {
			l1Plain += ` ${GIT_BRANCH_GLYPH} ${branch}`;
			l1Styled += ` ${theme.fg("dim", GIT_BRANCH_GLYPH)} ${theme.fg("muted", branch)}`;
		}
		if (sessionName) {
			l1Plain += ` • ${sessionName}`;
			l1Styled += theme.fg("dim", ` • ${sessionName}`);
		}
		const nSub = activeSubagentCount();
		// Right cluster: the view dial always, the live subagent count when there
		// is one. The dial's glyph fills up as the view widens, so its position on
		// the three-stop scale reads without parsing the word.
		const viewGlyph = TOOL_OUTPUT_VIEW_GLYPHS[this.toolOutputView];
		const rightParts: Array<{ plain: string; styled: string }> = [];
		if (nSub > 0) {
			rightParts.push({
				plain: `◇${nSub} running`,
				styled: theme.fg("accent", `◇${nSub}`) + theme.fg("dim", " running"),
			});
		}
		rightParts.push({
			plain: `${viewGlyph} ${this.toolOutputView}`,
			styled: theme.fg("dim", `${viewGlyph} ${this.toolOutputView}`),
		});
		const l1RightPlain = rightParts.map((p) => p.plain).join("  ");
		const l1RightStyled = rightParts.map((p) => p.styled).join("  ");
		const line1 = assembleLine(width, l1Plain, l1Styled, l1RightPlain, l1RightStyled);

		// ── Line 2 — session vitals ─────────────────────────────────────────────
		// A context-fill gauge (coloured by proximity to the auto-compact trip
		// point) leads, then token/cost deltas, with the model + thinking level
		// flush right. Numbers read in muted, labels/arrows in dim — a legible
		// hierarchy in place of the old uniform grey.
		let thresholdPercent: number | undefined;
		if (this.autoCompactEnabled && contextWindow > 0) {
			const reserveTokens = this.session.settingsManager.getCompactionSettings().reserveTokens;
			const effective = contextWindow - reserveTokens;
			if (effective > 0) thresholdPercent = (effective / contextWindow) * 100;
		}
		const errorLevel = thresholdPercent !== undefined ? thresholdPercent - 3 : 90;
		const warnLevel = thresholdPercent !== undefined ? thresholdPercent - 10 : 70;
		const autoIndicator =
			thresholdPercent !== undefined
				? ` auto@${thresholdPercent.toFixed(0)}%`
				: this.autoCompactEnabled
					? " auto"
					: "";

		const gauge = contextGauge(contextPercentValue, errorLevel, warnLevel);
		const pctText = contextPercent === "?" ? "?" : `${contextPercent}%`;
		const pctColor =
			contextPercentValue >= errorLevel ? "error" : contextPercentValue >= warnLevel ? "warning" : "muted";
		const winText = `${formatTokens(contextWindow)}${autoIndicator}`;

		const segs: Array<{ plain: string; styled: string }> = [
			{
				plain: `${gauge.plain} ${pctText} ${winText}`,
				styled: `${gauge.styled} ${theme.fg(pctColor, pctText)} ${theme.fg("dim", winText)}`,
			},
		];
		const arrow = (a: string, n: number) => ({
			plain: `${a}${formatTokens(n)}`,
			styled: theme.fg("dim", a) + theme.fg("muted", formatTokens(n)),
		});
		if (totalInput) segs.push(arrow("↑", totalInput));
		if (totalOutput) segs.push(arrow("↓", totalOutput));
		if (totalCacheRead) segs.push(arrow("R", totalCacheRead));
		if (totalCacheWrite) segs.push(arrow("W", totalCacheWrite));
		const usingSubscription = state.model ? this.session.modelRegistry.isUsingOAuth(state.model) : false;
		if (totalCost || usingSubscription) {
			const costStr = `$${totalCost.toFixed(3)}${usingSubscription ? " (sub)" : ""}`;
			segs.push({ plain: costStr, styled: theme.fg("muted", costStr) });
		}
		const l2Plain = segs.map((s) => s.plain).join("  ");
		const l2Styled = segs.map((s) => s.styled).join("  ");

		// Right: model, thinking level, and provider (when several are configured).
		const modelName = state.model?.id || "no-model";
		let r2Plain = modelName;
		let r2Styled = theme.fg("muted", modelName);
		if (state.model?.reasoning) {
			const tl = state.thinkingLevel || "off";
			const tstr = tl === "off" ? "thinking off" : tl;
			r2Plain += ` • ${tstr}`;
			r2Styled += theme.fg("dim", ` • ${tstr}`);
		}
		if (this.footerData.getAvailableProviderCount() > 1 && state.model) {
			// Prepend the provider only when the whole right cluster still fits.
			const withProv = `(${state.model.provider}) ${r2Plain}`;
			if (visibleWidth(l2Plain) + 2 + visibleWidth(withProv) <= width) {
				r2Plain = withProv;
				r2Styled = theme.fg("dim", `(${state.model.provider}) `) + r2Styled;
			}
		}
		const line2 = assembleLine(width, l2Plain, l2Styled, r2Plain, r2Styled);

		// One row: the mark and mode from line 1, the context gauge from line 2,
		// and the model flush right. Everything dropped — path, branch, token
		// arrows, cost — is a press of the dial away, and none of it is something
		// you read at a glance.
		if (this.density === "line") {
			const gaugeSeg = segs[0];
			const compactPlain = `${brand}  ${gaugeSeg.plain}`;
			const compactStyled = `${brandStyled}  ${gaugeSeg.styled}`;
			return [assembleLine(width, compactPlain, compactStyled, r2Plain, r2Styled), ...this.transientLines(width)];
		}

		const lines = [line1, line2];

		return [...lines, ...this.transientLines(width)];
	}

	/**
	 * Rows that are only there while something is happening, at either density.
	 *
	 * Extension statuses and startup progress are reports on work in flight, so
	 * they survive the compact stop — a download bar that vanished because you
	 * wanted more transcript would be the dial hiding something you cannot get
	 * back by waiting. At `bare` the whole footer is gone and so are these, which
	 * is the one place the dial does cost you something; that is what `bare`
	 * means.
	 */
	private transientLines(width: number): string[] {
		const lines: string[] = [];

		// Extension statuses on a single line, sorted by key alphabetically.
		const extensionStatuses = this.footerData.getExtensionStatuses();
		if (extensionStatuses.size > 0) {
			const sortedStatuses = Array.from(extensionStatuses.entries())
				.sort(([a], [b]) => a.localeCompare(b))
				.map(([, text]) => sanitizeStatusText(text));
			const statusLine = sortedStatuses.join(" ");
			// Truncate to terminal width with dim ellipsis for consistency with footer style
			lines.push(truncateToWidth(statusLine, width, theme.fg("dim", "...")));
		}

		// Transient startup progress (first-run tool downloads, index build): one
		// determinate bar per entry, cleared as each settles. Width-clamped like the
		// status line so the footer never overflows.
		for (const entry of startupProgress.list()) {
			lines.push(truncateToWidth(renderStartupLine(entry), width, theme.fg("dim", "…")));
		}

		return lines;
	}
}
